import { useState } from "react";
import { X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function QuoteModal({ isOpen, onClose }) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    full_name: "",
    email: "",
    phone: "",
    company: "",
    product_type: "",
    quantity: "",
    delivery_address: "",
    message: "",
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await axios.post(`${API}/quotes`, formData);
      toast.success("Demande de devis envoyée avec succès! Nous vous contacterons sous peu.");
      setFormData({
        full_name: "",
        email: "",
        phone: "",
        company: "",
        product_type: "",
        quantity: "",
        delivery_address: "",
        message: "",
      });
      onClose();
    } catch (error) {
      console.error("Erreur:", error);
      toast.error("Erreur lors de l'envoi. Veuillez réessayer.");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4" data-testid="quote-modal">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/70"
        onClick={onClose}
        data-testid="quote-modal-backdrop"
      />

      {/* Modal */}
      <div className="relative z-[101] bg-white w-full max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="quote-modal-content">
        {/* Header */}
        <div className="sticky top-0 bg-[#1E3A5F] text-white px-6 py-4 flex justify-between items-center">
          <h2 className="font-oswald text-2xl font-bold uppercase tracking-wider">
            Demande de Devis
          </h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-white/10 transition-colors"
            data-testid="quote-modal-close"
            aria-label="Fermer"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Full Name */}
            <div className="space-y-2">
              <Label htmlFor="full_name" className="text-sm font-medium text-slate-700">
                Nom Complet *
              </Label>
              <Input
                id="full_name"
                type="text"
                required
                value={formData.full_name}
                onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                className="rounded-none border-slate-300 focus:border-[#1E3A5F] focus:ring-[#1E3A5F]"
                placeholder="Votre nom complet"
                data-testid="quote-input-name"
              />
            </div>

            {/* Email */}
            <div className="space-y-2">
              <Label htmlFor="email" className="text-sm font-medium text-slate-700">
                Email *
              </Label>
              <Input
                id="email"
                type="email"
                required
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="rounded-none border-slate-300 focus:border-[#1E3A5F] focus:ring-[#1E3A5F]"
                placeholder="votre@email.com"
                data-testid="quote-input-email"
              />
            </div>

            {/* Phone */}
            <div className="space-y-2">
              <Label htmlFor="phone" className="text-sm font-medium text-slate-700">
                Téléphone *
              </Label>
              <Input
                id="phone"
                type="tel"
                required
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                className="rounded-none border-slate-300 focus:border-[#1E3A5F] focus:ring-[#1E3A5F]"
                placeholder="+221 XX XXX XX XX"
                data-testid="quote-input-phone"
              />
            </div>

            {/* Company */}
            <div className="space-y-2">
              <Label htmlFor="company" className="text-sm font-medium text-slate-700">
                Entreprise
              </Label>
              <Input
                id="company"
                type="text"
                value={formData.company}
                onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                className="rounded-none border-slate-300 focus:border-[#1E3A5F] focus:ring-[#1E3A5F]"
                placeholder="Nom de l'entreprise (optionnel)"
                data-testid="quote-input-company"
              />
            </div>

            {/* Product Type */}
            <div className="space-y-2">
              <Label htmlFor="product_type" className="text-sm font-medium text-slate-700">
                Type de Produit *
              </Label>
              <Select
                value={formData.product_type}
                onValueChange={(value) => setFormData({ ...formData, product_type: value })}
                required
              >
                <SelectTrigger className="rounded-none border-slate-300" data-testid="quote-select-product">
                  <SelectValue placeholder="Sélectionnez un produit" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="granulats-0-3">Granulats de Silex 0/3</SelectItem>
                  <SelectItem value="granulats-3-8">Granulats de Silex 3/8</SelectItem>
                  <SelectItem value="granulats-8-16">Granulats de Silex 8/16</SelectItem>
                  <SelectItem value="agglos-10">Agglos 10x20x40</SelectItem>
                  <SelectItem value="agglos-15">Agglos 15x20x40</SelectItem>
                  <SelectItem value="agglos-20">Agglos 20x20x40</SelectItem>
                  <SelectItem value="mixte">Commande Mixte</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Quantity */}
            <div className="space-y-2">
              <Label htmlFor="quantity" className="text-sm font-medium text-slate-700">
                Quantité Estimée *
              </Label>
              <Input
                id="quantity"
                type="text"
                required
                value={formData.quantity}
                onChange={(e) => setFormData({ ...formData, quantity: e.target.value })}
                className="rounded-none border-slate-300 focus:border-[#1E3A5F] focus:ring-[#1E3A5F]"
                placeholder="Ex: 10 m³, 500 unités..."
                data-testid="quote-input-quantity"
              />
            </div>
          </div>

          {/* Delivery Address */}
          <div className="space-y-2">
            <Label htmlFor="delivery_address" className="text-sm font-medium text-slate-700">
              Adresse de Livraison *
            </Label>
            <Input
              id="delivery_address"
              type="text"
              required
              value={formData.delivery_address}
              onChange={(e) => setFormData({ ...formData, delivery_address: e.target.value })}
              className="rounded-none border-slate-300 focus:border-[#1E3A5F] focus:ring-[#1E3A5F]"
              placeholder="Adresse complète de livraison"
              data-testid="quote-input-address"
            />
          </div>

          {/* Message */}
          <div className="space-y-2">
            <Label htmlFor="message" className="text-sm font-medium text-slate-700">
              Message Complémentaire
            </Label>
            <Textarea
              id="message"
              value={formData.message}
              onChange={(e) => setFormData({ ...formData, message: e.target.value })}
              className="rounded-none border-slate-300 focus:border-[#1E3A5F] focus:ring-[#1E3A5F] min-h-[100px]"
              placeholder="Détails supplémentaires sur votre projet..."
              data-testid="quote-input-message"
            />
          </div>

          {/* Submit Button */}
          <Button
            type="submit"
            disabled={isSubmitting}
            className="w-full bg-[#FFD100] text-[#1E3A5F] hover:bg-[#E6BC00] rounded-none py-4 font-bold uppercase tracking-wider"
            data-testid="quote-submit-btn"
          >
            {isSubmitting ? (
              <span className="flex items-center justify-center gap-2">
                <span className="spinner" />
                Envoi en cours...
              </span>
            ) : (
              "Envoyer la Demande"
            )}
          </Button>
        </form>
      </div>
    </div>
  );
}
